'use client'

import React, { useEffect, useRef, useState } from 'react'
import AppStoreLink from '@/app/components/AppStoreLink'
import { track } from '@/utils/analytics'

/**
 * Sticky mobile App Store CTA.
 *
 * Why: GA4 shows ~3-second engagement time and the existing blog template puts
 * the App Store CTA only at the bottom of the article. Mobile readers scroll,
 * read, and bounce without seeing it. A persistent (but dismissable) bottom
 * bar gives the reader a second touchpoint after they've engaged enough to
 * scroll past the intro.
 *
 * Behavior:
 *  - Mobile only (`md:hidden`). Desktop has enough screen real estate for
 *    the inline CTAs to do their job.
 *  - Hidden on initial render. Appears once the reader has scrolled ~25% of
 *    the page — proxy for "they're actually reading."
 *  - Dismissable. Dismissal persists for the session (sessionStorage), so a
 *    user who closes it on one article doesn't get re-pestered on the next.
 *  - Tracks `sticky_app_cta_click` for surface behavior; AppStoreLink owns the
 *    canonical destination event and routes Android to the waitlist.
 *  - Respects iOS safe-area inset so it sits above the home-bar.
 *  - Lightweight: passive scroll listener, no framer-motion, ~1.2 KB gzip.
 */
export interface StickyMobileAppCTAProps {
  /** Stable surface label, e.g. `blog_sticky_mobile`, `home_sticky_mobile`. */
  source: string
  /** Scroll % (0–1) past which the bar appears. Default 0.25. */
  appearAfterPct?: number
  /** Optional extra params for the tracking event (post_topic, post_slug…). */
  trackingParams?: Record<string, string | number | boolean | undefined>
  /** Short headline shown to the left of the button. */
  text?: string
  /** Button label. Keep ≤ 12 chars to fit on small viewports. */
  buttonLabel?: string
}

const SESSION_DISMISS_KEY = 'mp_sticky_cta_dismissed'

export default function StickyMobileAppCTA({
  source,
  appearAfterPct = 0.25,
  trackingParams,
  text = 'MicroPlastics',
  buttonLabel = 'Get the App',
}: StickyMobileAppCTAProps) {
  const [visible, setVisible] = useState(false)
  const [dismissed, setDismissed] = useState(false)
  const initialisedRef = useRef(false)

  // Read the session-level dismiss flag once on mount. We don't read it on
  // every render because sessionStorage access is a synchronous DOM call.
  useEffect(() => {
    if (initialisedRef.current) return
    initialisedRef.current = true
    try {
      if (sessionStorage.getItem(SESSION_DISMISS_KEY) === '1') {
        // Deliberate. sessionStorage is browser-only; reading it during render
        // would differ between server and client and break hydration.
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setDismissed(true)
      }
    } catch {
      // Private-mode Safari throws on sessionStorage access. Ignore.
    }
  }, [])

  // Passive scroll listener. We stop listening as soon as the bar becomes
  // visible — no point paying for scroll events forever.
  useEffect(() => {
    if (dismissed || visible) return
    const onScroll = () => {
      const docHeight = document.documentElement.scrollHeight - window.innerHeight
      if (docHeight <= 0) return
      const pct = window.scrollY / docHeight
      if (pct >= appearAfterPct) {
        setVisible(true)
      }
    }
    // Check once in case the user lands deep-scrolled (e.g. anchor link).
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [dismissed, visible, appearAfterPct])

  if (dismissed || !visible) return null

  const handleDismiss = () => {
    setDismissed(true)
    try {
      sessionStorage.setItem(SESSION_DISMISS_KEY, '1')
    } catch {}
  }

  const handleCtaClick = () => {
    // Keep the surface-specific event for historical continuity. AppStoreLink
    // owns the canonical destination event and platform split.
    track('sticky_app_cta_click', {
      source,
      cta_position: 'sticky',
      cta_copy: buttonLabel,
      app_store_campaign_token: source,
      ...trackingParams,
    })
  }

  return (
    <div
      role="region"
      aria-label="Download the MicroPlastics app"
      className="md:hidden fixed inset-x-0 bottom-0 z-40 bg-white/95 backdrop-blur border-t border-black/10 shadow-[0_-4px_16px_rgba(0,0,0,0.04)] px-3 py-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] flex items-center gap-2"
    >
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-900 leading-tight truncate">
          {text}
        </p>
        <p className="text-2xs text-gray-500 leading-tight">
          Free download · In-app purchases
        </p>
      </div>
      <AppStoreLink
        source={source}
        ctaPosition="sticky"
        trackingParams={{
          cta_copy: buttonLabel,
          app_store_campaign_token: source,
          ...trackingParams,
        }}
        onClick={handleCtaClick}
        className="shrink-0 inline-flex items-center gap-1.5 bg-ink text-cream text-sm font-semibold px-4 py-2 rounded-lg active:scale-95 transition-transform"
      >
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.08 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
        </svg>
        {buttonLabel}
      </AppStoreLink>
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Dismiss app download banner"
        className="shrink-0 -mr-1 w-11 h-11 inline-flex items-center justify-center rounded-lg text-gray-500 hover:text-gray-800 hover:bg-gray-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1E6B47]"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  )
}
